import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Controlled as CodeMirror } from "react-codemirror2";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/dracula.css";
import "codemirror/mode/javascript/javascript";
import "codemirror/mode/python/python";
import "codemirror/mode/htmlmixed/htmlmixed";
import "codemirror/mode/css/css";
import { useHistoryContext } from "../context/HistoryContext";
import lintMessages from "../utils/lintMessages";
import htmlLintMessages, { deduceHtmlRuleId } from "../utils/htmlLintMessages";
import cssLintMessages from "../utils/cssLintMessages";
import pythonLintMessages from "../utils/pythonLintMessages";

const CodeAnalysis = () => {
  const [code, setCode] = useState("");
  const [language, setLanguage] = useState("");
  const [result, setResult] = useState("");
  const [correctedCode, setCorrectedCode] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [fixableIssues, setFixableIssues] = useState([]);
  const [nonFixableIssues, setNonFixableIssues] = useState([]);
  const { addToHistory } = useHistoryContext(); // Função para adicionar ao histórico

  // Detecta a linguagem
  const detectLanguage = async () => {
    if (code.trim()) {
      try {
        const response = await axios.post(
          "https://codefort.onrender.com/api/detect-language",
          { code }
        );
        setLanguage(response.data.language || "Desconhecida");
      } catch (error) {
        setLanguage("Erro ao detectar");
      }
    } else {
      setLanguage("");
    }
  };

  useEffect(() => {
    detectLanguage();
    setResult("");
    setCorrectedCode("");
    setFixableIssues([]);
    setNonFixableIssues([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Análise de código
  const handleAnalyzeCode = async () => {
    if (!code.trim()) return;

    setIsAnalyzing(true);
    setResult("");
    setCorrectedCode("");
    setFixableIssues([]);
    setNonFixableIssues([]);

    try {
      const response = await axios.post("https://codefort.onrender.com/api/analyze", {
        code,
        detectedLanguage: language,
      });

      const {
        fixableIssues = [],
        nonFixableIssues = [],
        correctedCode = "",
      } = response.data.analysis || {};

      setResult(response.data.analysis?.results || "");
      setCorrectedCode(correctedCode);
      setFixableIssues(fixableIssues);
      setNonFixableIssues(nonFixableIssues);

      // Adiciona a análise ao histórico
      const historyEntry = {
        date: new Date().toISOString(),
        type: "CodeAnalysis",
        details: response.data.analysis?.message || "Análise concluída.",
      };
      addToHistory(historyEntry);
    } catch (error) {
      setResult("Erro ao analisar o código.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Análise de Código
      </Typography>

      {language && (
        <Paper
          elevation={3}
          sx={{ p: 2, mb: 2, backgroundColor: "#f0f0f0", color: "#333" }}
        >
          <strong>Linguagem Detectada:</strong> {language.toUpperCase()}
        </Paper>
      )}

      <CodeMirror
        value={code}
        options={{
          mode: language || "javascript",
          theme: "dracula",
          lineNumbers: true,
        }}
        onBeforeChange={(editor, data, value) => setCode(value)}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleAnalyzeCode}
        disabled={!code || isAnalyzing}
        sx={{ mt: 2 }}
      >
        {isAnalyzing ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Analisar Código"
        )}
      </Button>

      {result && (
        <Paper elevation={3} sx={{ p: 2, mt: 3, whiteSpace: "pre-wrap" }}>
          <Typography variant="h6" gutterBottom>
            Resultado:
          </Typography>

          {/* Exibe mensagem específica para erros de parsing */}
          {nonFixableIssues?.length > 0 &&
          nonFixableIssues.some((issue) => issue.ruleId === "parsing-error") ? (
            <>
              <Typography
                variant="body1"
                sx={{ color: "red", fontWeight: "bold", mt: 2 }}
              >
                Corrija os seguintes erros de parsing antes de continuar:
              </Typography>
              <ul>
                {nonFixableIssues
                  .filter((issue) => issue.ruleId === "parsing-error")
                  .map((issue, idx) => (
                    <li key={idx}>
                      <strong>{`Linha ${issue.line || "?"}, Coluna ${
                        issue.column || "?"
                      }:`}</strong>{" "}
                      {issue.message}
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 1 }}
                      >
                        Certifique-se de que o código está bem formatado e
                        correto.
                      </Typography>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            <>
              {/* Fluxo normal para outros tipos de erros */}
              {fixableIssues?.length === 0 && nonFixableIssues?.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: "green", fontWeight: "bold" }}
                >
                  Nenhum problema encontrado. O código está de acordo com as
                  boas práticas!
                </Typography>
              ) : (
                <>
                  {nonFixableIssues?.length > 0 && (
                    <>
                      <Typography
                        variant="body1"
                        sx={{ color: "red", fontWeight: "bold", mt: 2 }}
                      >
                        Foram encontrados problemas que não podem ser corrigidos
                        automaticamente:
                      </Typography>
                      <ul>
                        {nonFixableIssues.map((issue, idx) => {
                          // 1) Se ruleId vier undefined ou vazio, tentamos deduzir
                          let deducedRuleId = issue.ruleId;
                          if (!issue.ruleId) {
                            deducedRuleId =
                              deduceHtmlRuleId(issue.message) || "";
                          }

                          if (!issue.ruleId) {
                            if (issue.message.includes("Tag must be paired")) {
                              deducedRuleId = "tag-pair";
                            }
                            // se quiser mapear mais mensagens "special characters must be escaped", etc.
                          }

                          return (
                            <li key={idx}>
                              <strong>{`Linha ${issue.line || "?"}, Coluna ${
                                issue.column || "?"
                              }:`}</strong>{" "}
                              {issue.message}
                              {lintMessages[deducedRuleId] ||
                              htmlLintMessages[deducedRuleId] ||
                              pythonLintMessages[deducedRuleId] ||
                              cssLintMessages[deducedRuleId] ? (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ mt: 1 }}
                                >
                                  {lintMessages[deducedRuleId] ||
                                    htmlLintMessages[deducedRuleId] ||
                                    pythonLintMessages[deducedRuleId] ||
                                    cssLintMessages[deducedRuleId]}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ mt: 1 }}
                                >
                                  Descrição não encontrada para essa regra.
                                </Typography>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}

                  {fixableIssues?.length > 0 && (
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ mt: 2 }}
                      onClick={() => {
                        setResult(
                          `Código corrigido automaticamente:\n${correctedCode}`
                        );
                        setCorrectedCode(correctedCode);
                      }}
                    >
                      Clique aqui para corrigir os problemas automaticamente.
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CodeAnalysis;
